import { ecorEnv } from '@/../environments/environment';
import { PortalUser } from '@/common/components/ecorr-store/portal-user/portal-user';
import { portalUserSelector } from '@/common/components/ecorr-store/portal-user/portal-user.selector';
import { PortalUserState } from '@/common/components/ecorr-store/portal-user/portal-user.state';
import { ParadataEventService } from '@/common/paradata-event/paradata-event.service';
import { FileUtilService } from '@/common/utils/file-util.service';
import { AddSurveyDialogData } from '@/surveys/dialogs-data/add-survey-dialog-data';
import { AddSurveyDialogService } from '@/surveys/services/add-survey-dialog.service';
import { EnableCookiesDialogData } from '@/surveys/dialogs-data/enable-cookies-dialog-data';
import { EnableCookiesDialogService } from '@/surveys/services/enable-cookies-dialog.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { NotificationOptDialogData } from '@/surveys/dialogs-data/notification-opt-dialog-data';
import { NotificationOptDialogService } from '@/surveys/services/notification-opt-dialog.service';
import { NotificationOptDialogHttpService } from '@/surveys/services/notification-opt-dialog-http.service';
import { WelcomeSurveyDialogService } from '@/surveys/services/welcome-survey-dialog.service';
import { WelcomeSurveyDialogData } from '@/surveys/dialogs-data/welcome-survey-dialog-data';
import { EventService } from '@/surveys/services/event.service';
import { SurveyCard } from '@/surveys/states/surveys/survey-card';
import { Component, Input, inject, signal, computed, DestroyRef } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SurveyViewsBaseService } from '@/surveys/services/survey-views-base.service';

@Component({
    selector: 'app-survey-views-base',
    template: '<div>survey-views-base works!</div>',
    standalone: true,
    providers: [
        AddSurveyDialogService,
        EnableCookiesDialogService,
        ParadataEventService,
        SurveyViewsBaseService,
        NotificationOptDialogService,
        NotificationOptDialogHttpService,
        WelcomeSurveyDialogService,
        FileUtilService,
        EventService
    ]
})
export class SurveyViewsBaseComponent {
    // Services using inject()
    protected matDialog = inject(MatDialog);
    protected addSurveyDialogService = inject(AddSurveyDialogService);
    protected enableCookiesDialogService = inject(EnableCookiesDialogService);
    protected paradataEventService = inject(ParadataEventService);
    protected surveyViewsBaseService = inject(SurveyViewsBaseService);
    protected notificationOptDialogService = inject(NotificationOptDialogService);
    protected notificationOptDialogHttpService = inject(NotificationOptDialogHttpService);
    protected welcomeSurveyDialogService = inject(WelcomeSurveyDialogService);
    protected fileUtilService = inject(FileUtilService);
    protected eventService = inject(EventService);
    protected store = inject(Store<PortalUserState>);
    protected destroyRef = inject(DestroyRef);

    constructor() {
        this.event$ = this.eventService.eventListner()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(
                event => {
                    if (event.surveyInstanceId) {
                        this.surveyInstanceId.set(event.surveyInstanceId);
                    }
                }
            );
        this.user$ = this.store.select(portalUserSelector)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(
                user => {
                    this.user.set(user);
                }
            );
    }

    @Input('active')
    active: boolean = true;

    @Input('surveys')
    surveys: SurveyCard[];

    @Input('overdue-message')
    overdueMessage: string;

    // Signals for reactive state
    order = signal('');
    reverse = signal(false);
    surveyInstanceId = signal<number>(0);
    user = signal<PortalUser | null>(null);
    loading = signal(false);
    attachmentId = signal<string>('');
    fileName = signal<string>('');
    fileType = signal<string>('');
    base64 = signal<string>('');
    showCanister = signal(false);
    canisterType = signal<string>('');
    canisterAltText = signal<string>('');
    isImage = signal(false);
    isExcel = signal(false);
    isWord = signal(false);
    canisterSite = signal<string>('');
    fileTypeFound = signal(false);
    fileTypeClass = signal<string>('');

    // Computed signals
    currentOrder = computed(() => this.order());
    currentReverse = computed(() => this.reverse());
    currentSurveyInstanceId = computed(() => this.surveyInstanceId());
    currentUser = computed(() => this.user());
    isLoading = computed(() => this.loading());
    currentAttachmentId = computed(() => this.attachmentId());
    currentFileName = computed(() => this.fileName());
    currentFileType = computed(() => this.fileType());
    currentBase64 = computed(() => this.base64());
    shouldShowCanister = computed(() => this.showCanister());
    currentCanisterType = computed(() => this.canisterType());
    currentCanisterAltText = computed(() => this.canisterAltText());
    isImageFile = computed(() => this.isImage());
    isExcelFile = computed(() => this.isExcel());
    isWordFile = computed(() => this.isWord());
    currentCanisterSite = computed(() => this.canisterSite());
    isFileTypeFound = computed(() => this.fileTypeFound());
    currentFileTypeClass = computed(() => this.fileTypeClass());

    // Legacy subscriptions for compatibility
    user$: Subscription;

    cancelConfirm$: Subscription;
    event$: Subscription;
    updatePaperlessOpt$: Subscription;
    getPortalCanisterData$: Subscription;
    getResourceBase64$: Subscription;
    getResourceFilename$: Subscription;
    addSurveyText = 'Received a new survey request? Add it with your Authentication Code to get started.';

    fileTypes = ['jpg', 'jpeg', 'gif', 'png',
        'xls', 'xlsx', 'csv',
        'doc', 'xdoc',
        'zip', 'txt', 'pdf'
    ];

    acctMgrTooltipHtml = `
    An Account Manager will
    serve as your Census point of
    contact for all questions and
    concerns regarding your
    survey(s).`;

    exportCalTooltipHtml = `
    An email with an .ICS 
    attachment of your survey 
    due dates will be sent to 
    the email address on your 
    account.`;

    exportCalInstrTooltipHtml = `
    Import instructions specific 
    to your email provider.`;

    enableCookiesInfo: EnableCookiesDialogData = {
        title: 'Please Enable Cookies',
        instructions: 'You have <b>cookies disabled</b>; you need to re-enable them for this site to interact with it. <br>',
    };
    
    testFn() {
        this.eventService.emitEvent({
            canisterTriggered: {
                surveyId: 784,
                canisterCode: 1,
                trigger: 'AuthCodeBurned'
            }
        });
    }
    
    addSurvey() {
        const options: AddSurveyDialogData = {
            title: 'ENTER AUTHENTICATION CODE',
            cancelButtonLabel: 'Cancel Request',
            confirmButtonLabel: 'Begin'
        };
        this.addSurveyDialogService.open(options);

        this.cancelConfirm$ = this.addSurveyDialogService.confirmed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(
                res => {
                    this.updatePaperlessOpt$ = this.notificationOptDialogHttpService.updatePaperlessOptToN(this.user()?.userAccountId,  res.surveyInstanceId)
                        .pipe(takeUntilDestroyed(this.destroyRef))
                        .subscribe(
                            (response) => {
                            },
                            (error: any) => {
                                console.error('survey-views-base updatePaperlessOptToN error', error);
                            }
                        );

                    //Paperless Opt In/Out - ACES, COS/ASM
                    if (res.paperless) {
                        const notification: NotificationOptDialogData = {
                            title: res.surveyType + ' Paperless Notification Opt In/Out',
                            surveyName: res.surveyName,
                            surveyTypeName: res.surveyType,
                            surveyInstanceId: res.surveyInstanceId,
                            cancelButtonLabel: 'No',
                            confirmButtonLabel: 'Yes'
                        };
                        this.notificationOptDialogService.open(notification);
                    }

                    //Survey Specific Welcome Message - CFS
                    if (res.preCanvasSurvey) {
                        let options: WelcomeSurveyDialogData = {
                            title: 'Welcome ' + this.user()?.firstName + ' ' + this.user()?.lastName,
                            subheading: 'Thank you for adding ' + res.surveyName + '.',
                            instructions: 'You can use the send a message feature under options of your ' + res.surveyType + ' prework survey card to contact an analyst with any questions. Click \'OK\' to close this message.',
                            confirmButtonLabel: 'OK'
                        };
                        if (res.surveyType === 'CFS') {
                            options = {
                                title: 'Welcome ' + this.user()?.firstName + ' ' + this.user()?.lastName,
                                subheading: 'Thank you for requesting the company locations spreadsheet.',
                                instructions: 'Your company spreadsheet is currently being generated and will be ready momentarily. Please click \'Access Messages\' on your Commodity Flow survey card and select the message \'CFS Prescreener Request\' to view the instructions and download the spreadsheet. Click \'OK\' to close this message.<br>NOTE: The message may take up to 2 minutes to generate.',
                                confirmButtonLabel: 'OK'
                            };
                        }
                        this.welcomeSurveyDialogService.open(options);
                    }

                    this.eventService.emitEvent({
                        surveyAdded: true
                    });
                });
    }

    reorderSurveys(attrib: string) {
        this.updateSortState(attrib);

        if (this.surveys && Array.isArray(this.surveys)) {
            this.surveys = this.sortSurveyArray(this.surveys, attrib);
        }
    }

    protected updateSortState(attrib: string) {
        if (this.order() !== attrib) {
            this.reverse.set(false);
            this.order.set(attrib);
        } else {
            this.reverse.set(!this.reverse());
        }
    }

    protected sortSurveyArray(surveys: SurveyCard[], attrib: string) {
        return [...surveys].sort((a, z) => {

            if (typeof a[attrib] === 'undefined' || a[attrib] == null) {
                if (this.reverse()) {
                    return 1;
                } else {
                    return -1;
                }
            }

            if (typeof z[attrib] === 'undefined' || z[attrib] == null) {
                if (this.reverse()) {
                    return -1;
                } else {
                    return 1;
                }
            }

            if (this.reverse()) {
                if (attrib === 'currentDueDate') {
                    return z[attrib].getTime() - a[attrib].getTime();
                } else {
                    return String(z[attrib]).localeCompare(String(a[attrib]), 'en', { sensitivity: 'base' });
                }
            } else {
                if (attrib === 'currentDueDate') {
                    return a[attrib].getTime() - z[attrib].getTime();
                } else {
                    return String(a[attrib]).localeCompare(String(z[attrib]), 'en', { sensitivity: 'base' });
                }
            }
        });
    }

    /**
     * Sorting algorithm for survey card sorting
     * */
    sortSurveysDefault(survA, survB) {
        // Sort first by status
        if (survA.formStatus !== survB.formStatus) {
            return survA.formStatus - survB.formStatus;
        } else {
            // Then due/extended date within each status
            const aExt = survA.originalDueDate.getTime() !== survA.currentDueDate.getTime();
            const bExt = survB.originalDueDate.getTime() !== survB.currentDueDate.getTime();

            // If no/two extension date(s), sort purely by current due date
            // If one extension date, put extended survey last
            if ((aExt && bExt) || (!aExt && !bExt)) {
                return survA.currentDueDate - survB.currentDueDate;
            } else {
                return !aExt && bExt ? -1 : 1;
            }
        }
    }

    clickToDownloadOrOpenNewSiteTab() {
        if (this.isImage()) {
            window.open(this.canisterSite(), '_blank');
        } else {
            // window.open(ecorEnv.apiEndpoint + '/secure/pega/attachments/' + this.attachmentId, 'downloadWindow');
            const link = document.createElement('a');
            link.setAttribute('type', 'hidden');
            link.href = ecorEnv.apiEndpoint + '/secure/pega/attachments/' + this.attachmentId();
            link.download = this.fileName();
            document.body.appendChild(link);
            link.click();
            link.remove();
        }
    }

    clickToDownload() {
        const link = document.createElement('a');
        link.setAttribute('type', 'hidden');
        link.href = ecorEnv.apiEndpoint + '/secure/pega/attachments/' + this.attachmentId();
        link.download = this.fileName();
        document.body.appendChild(link);
        link.click();
        link.remove();
    }

    destroy() {
        this.paradataEventService.flushParadataevents();
        // Subscriptions are now handled by takeUntilDestroyed, no manual unsubscribe needed here.
    }
}